import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { SERVICES } from "@/lib/content";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const TIMELINES = ["ASAP", "This month", "Next 1–3 months", "3–6 months", "Just planning"];

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "",
  location: "",
  timeline: "",
  details: "",
};

const fieldVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE, delay: i * 0.08 },
  }),
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.phone || form.phone.trim().length < 5) e.phone = "Enter your phone number.";
    if (!form.service) e.service = "Choose a service.";
    if (!form.location || form.location.trim().length < 2) e.location = "Where's the job?";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      setForm(initial);
      toast.success("Quote request sent. We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      className="relative bg-[color:var(--te-bg)] px-5 md:px-10 py-24 md:py-32 border-t border-white/10"
      data-testid="quote-section"
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-[color:var(--te-primary)] rotate-45" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--te-primary)] font-display">
              Request a quote
            </span>
          </div>
          <h2
            className="font-display font-black uppercase text-white tracking-tighter leading-[0.88]"
            style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)" }}
            data-testid="quote-heading"
          >
            Start your <br />
            <span className="font-serif-italic tracking-normal text-[color:var(--te-primary)]">
              project.
            </span>
          </h2>
          <p className="mt-8 text-white/70 font-body text-base md:text-lg max-w-[46ch] leading-relaxed">
            Tell us about the site and what you need moved. We&rsquo;ll respond with a
            walk-through, timeline and honest costing — usually within one
            business day.
          </p>

          <div className="mt-12 space-y-6">
            {[
              ["Phone", "0417 984 679"],
              ["Email", "Trilogyearthworks@gmail.com"],
              ["Yard", "Rockhampton, QLD · Central Queensland"],
              ["Hours", "Mon–Sat · 06:30–17:30"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 border-b border-white/10 pb-3">
                <span className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-display">{k}</span>
                <span className="text-sm text-white font-body text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="border border-white/15 p-8 md:p-12"
              data-testid="quote-success"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 bg-[color:var(--te-primary)] rotate-45" />
                <span className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--te-primary)] font-display">
                  Received
                </span>
              </div>
              <h3 className="font-display font-black uppercase text-3xl md:text-5xl text-white tracking-tighter leading-none">
                Thanks — we&rsquo;ve got it.
              </h3>
              <p className="mt-4 text-white/70 font-body max-w-[52ch]">
                Your quote request is with the Trilogy team. We&rsquo;ll be in touch
                within one business day with next steps.
              </p>
              <button
                onClick={() => setDone(false)}
                className="te-btn te-btn-ghost mt-8"
                data-testid="quote-reset-button"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <motion.form
              onSubmit={onSubmit}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
              data-testid="quote-form"
              noValidate
            >
              <motion.div variants={fieldVariants} custom={0} className="md:col-span-1">
                <label className="te-label" htmlFor="q-name">Name *</label>
                <input
                  id="q-name"
                  className="te-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  data-testid="quote-input-name"
                />
                {errors.name && <p className="text-xs text-red-400 mt-1" data-testid="quote-error-name">{errors.name}</p>}
              </motion.div>

              <motion.div variants={fieldVariants} custom={1} className="md:col-span-1">
                <label className="te-label" htmlFor="q-email">Email *</label>
                <input
                  id="q-email"
                  type="email"
                  className="te-input"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com.au"
                  data-testid="quote-input-email"
                />
                {errors.email && <p className="text-xs text-red-400 mt-1" data-testid="quote-error-email">{errors.email}</p>}
              </motion.div>

              <motion.div variants={fieldVariants} custom={2}>
                <label className="te-label" htmlFor="q-phone">Phone *</label>
                <input
                  id="q-phone"
                  className="te-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="04XX XXX XXX"
                  data-testid="quote-input-phone"
                />
                {errors.phone && <p className="text-xs text-red-400 mt-1" data-testid="quote-error-phone">{errors.phone}</p>}
              </motion.div>

              <motion.div variants={fieldVariants} custom={3}>
                <label className="te-label" htmlFor="q-location">Site location *</label>
                <input
                  id="q-location"
                  className="te-input"
                  value={form.location}
                  onChange={set("location")}
                  placeholder="Suburb, town or address"
                  data-testid="quote-input-location"
                />
                {errors.location && <p className="text-xs text-red-400 mt-1" data-testid="quote-error-location">{errors.location}</p>}
              </motion.div>

              <motion.div variants={fieldVariants} custom={4}>
                <label className="te-label">Service required *</label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger
                    className="te-input !h-auto !rounded-none !border-0 !border-b !border-b-[color:var(--te-border)] !bg-transparent !px-0 !py-[0.85rem] focus:!border-b-[color:var(--te-primary)] focus:!ring-0 focus:!ring-offset-0"
                    data-testid="quote-select-service"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-white/15 bg-[#0f0f0f] text-white">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.key}
                        value={s.name}
                        data-testid={`quote-service-option-${s.key}`}
                        className="rounded-none focus:bg-[color:var(--te-primary)]/15 focus:text-white"
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.service && <p className="text-xs text-red-400 mt-1" data-testid="quote-error-service">{errors.service}</p>}
              </motion.div>

              <motion.div variants={fieldVariants} custom={5}>
                <label className="te-label">Timeline</label>
                <Select value={form.timeline} onValueChange={(v) => setForm((f) => ({ ...f, timeline: v }))}>
                  <SelectTrigger
                    className="te-input !h-auto !rounded-none !border-0 !border-b !border-b-[color:var(--te-border)] !bg-transparent !px-0 !py-[0.85rem] focus:!border-b-[color:var(--te-primary)] focus:!ring-0 focus:!ring-offset-0"
                    data-testid="quote-select-timeline"
                  >
                    <SelectValue placeholder="When do you need it?" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-white/15 bg-[#0f0f0f] text-white">
                    {TIMELINES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        className="rounded-none focus:bg-[color:var(--te-primary)]/15 focus:text-white"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>

              <motion.div variants={fieldVariants} custom={6} className="md:col-span-2">
                <label className="te-label" htmlFor="q-details">Project details</label>
                <textarea
                  id="q-details"
                  className="te-input min-h-[120px] resize-y"
                  value={form.details}
                  onChange={set("details")}
                  placeholder="Tell us about the site, scope, access, and any plans you have."
                  data-testid="quote-input-details"
                />
              </motion.div>

              <motion.div variants={fieldVariants} custom={7} className="md:col-span-2 mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
                <p className="text-xs text-white/50 font-body max-w-md">
                  By submitting you agree we may contact you about your enquiry. We don&rsquo;t share your details.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="te-btn te-btn-primary w-full sm:w-auto min-w-[220px] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="quote-form-submit"
                >
                  {submitting ? "Sending…" : "Send Request →"}
                </button>
              </motion.div>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
}
